import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { generateWhatsAppUrl } from '@utils/index'
import { APP_CONFIG } from '@config/index'
import { cn } from '@utils/cn'
import { useResponsiveSettings } from '@hooks/useResponsiveSettings'

type FloatingLayerItem = {
    id: string
    size: number
    left: string
    top: string
    background: string
    filter: string
    boxShadow?: string
    borderRadius?: string
    border?: string
    animate?: Record<string, number[]>
    transition?: {
        duration: number
        repeat: number
        repeatType?: 'reverse'
        ease: 'easeInOut' | 'linear'
        delay: number
    }
}

const createFloatingItems = (
    count: number,
    factory: (index: number) => FloatingLayerItem,
): FloatingLayerItem[] => Array.from({ length: count }, (_, index) => factory(index))

const Hero: React.FC = () => {
    const { isMobile, shouldReduceEffects } = useResponsiveSettings()

    const handleWhatsAppClick = () => {
        const message = '¡Hola! Me gustaría agendar una cita para servicios de óptica/dental 👓🦷'
        const url = generateWhatsAppUrl(APP_CONFIG.contact.whatsapp, message)
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    const handleScrollToServices = () => {
        const servicesSection = document.getElementById('servicios')
        if (servicesSection) {
            servicesSection.scrollIntoView({ behavior: 'smooth' })
        }
    }

    const largeOrbs = useMemo(
        () => createFloatingItems(shouldReduceEffects ? 2 : 5, (index) => ({
            id: `large-${index}`,
            size: shouldReduceEffects ? 160 + index * 40 : 220 + index * 50,
            left: `${10 + index * 18}%`,
            top: `${8 + (index % 3) * 24}%`,
            background: index % 2 === 0
                ? 'radial-gradient(circle, rgba(59, 130, 246, 0.18) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(6, 182, 212, 0.16) 0%, transparent 70%)',
            filter: `blur(${shouldReduceEffects ? 28 : 40}px)`,
            animate: shouldReduceEffects
                ? undefined
                : {
                    y: [0, 40 - index * 6, 0],
                    x: [0, index % 2 === 0 ? 30 : -30, 0],
                    scale: [1, 1.14, 1],
                },
            transition: shouldReduceEffects
                ? undefined
                : {
                    duration: 16 + index * 2,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                    delay: index * 0.4,
                },
        })),
        [shouldReduceEffects],
    )

    const mediumParticles = useMemo(
        () => createFloatingItems(shouldReduceEffects ? 6 : 10, (index) => ({
            id: `medium-${index}`,
            size: shouldReduceEffects ? 48 + index * 6 : 64 + index * 8,
            left: `${6 + (index * 9) % 88}%`,
            top: `${12 + (index * 11) % 72}%`,
            background: index % 3 === 0
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.24), rgba(147, 51, 234, 0.18))'
                : index % 3 === 1
                    ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.22), rgba(236, 72, 153, 0.18))'
                    : 'linear-gradient(135deg, rgba(244, 63, 94, 0.2), rgba(249, 115, 22, 0.18))',
            filter: `blur(${shouldReduceEffects ? 10 : 18}px)`,
            boxShadow: shouldReduceEffects ? 'none' : '0 0 45px rgba(59, 130, 246, 0.18)',
            animate: shouldReduceEffects
                ? undefined
                : {
                    y: [0, index % 2 === 0 ? -24 : 24, 0],
                    x: [0, index % 2 === 0 ? 16 : -16, 0],
                    opacity: [0.28, 0.55, 0.28],
                },
            transition: shouldReduceEffects
                ? undefined
                : {
                    duration: 12 + index,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                    delay: index * 0.25,
                },
        })),
        [shouldReduceEffects],
    )

    const sparkles = useMemo(
        () => createFloatingItems(shouldReduceEffects ? 10 : 18, (index) => ({
            id: `small-${index}`,
            size: shouldReduceEffects ? 10 + (index % 4) * 2 : 14 + (index % 5) * 3,
            left: `${4 + (index * 5) % 92}%`,
            top: `${6 + (index * 7) % 82}%`,
            background: index % 4 === 0
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.6), rgba(147, 51, 234, 0.6))'
                : index % 4 === 1
                    ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.6), rgba(236, 72, 153, 0.6))'
                    : index % 4 === 2
                        ? 'linear-gradient(135deg, rgba(244, 63, 94, 0.6), rgba(249, 115, 22, 0.6))'
                        : 'linear-gradient(135deg, rgba(250, 204, 21, 0.6), rgba(34, 197, 94, 0.6))',
            filter: 'blur(1px)',
            boxShadow: shouldReduceEffects ? 'none' : '0 0 24px rgba(59, 130, 246, 0.28)',
            animate: shouldReduceEffects
                ? undefined
                : {
                    y: [0, index % 2 === 0 ? -16 : 16, 0],
                    x: [0, index % 3 === 0 ? 12 : -12, 0],
                    scale: [1, 1.25, 1],
                    opacity: [0.35, 0.8, 0.35],
                },
            transition: shouldReduceEffects
                ? undefined
                : {
                    duration: 6 + index * 0.2,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                    delay: index * 0.15,
                },
        })),
        [shouldReduceEffects],
    )

    const shapes = useMemo(
        () => createFloatingItems(shouldReduceEffects ? 2 : 4, (index) => ({
            id: `shape-${index}`,
            size: shouldReduceEffects ? 88 + index * 18 : 120 + index * 24,
            left: `${12 + index * 22}%`,
            top: `${18 + (index % 2) * 30}%`,
            background: 'transparent',
            filter: shouldReduceEffects ? 'none' : 'blur(1px)',
            borderRadius: index % 2 === 0 ? '50%' : '18%',
            border: index % 2 === 0
                ? '2px solid rgba(59, 130, 246, 0.18)'
                : '2px solid rgba(236, 72, 153, 0.18)',
            boxShadow: shouldReduceEffects ? 'none' : '0 0 32px rgba(59, 130, 246, 0.14)',
            animate: shouldReduceEffects
                ? undefined
                : {
                    rotate: [0, 180, 360],
                    opacity: [0.18, 0.35, 0.18],
                    scale: [1, 1.08, 1],
                },
            transition: shouldReduceEffects
                ? undefined
                : {
                    duration: 18 + index * 3,
                    repeat: Infinity,
                    ease: 'linear',
                    delay: index,
                },
        })),
        [shouldReduceEffects],
    )

    return (
        <>
            <Helmet>
                <title>{APP_CONFIG.name} | {APP_CONFIG.tagline}</title>
                <meta name="description" content={APP_CONFIG.description} />
                <link rel="canonical" href={APP_CONFIG.site.url} />
                <meta property="og:url" content={APP_CONFIG.site.url} />
            </Helmet>

            <section
                id="inicio"
                className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50 pt-16 sm:pt-20 pb-12"
            >
                {/* Advanced 3D Animated Background with Multiple Layers */}
                <div className="absolute inset-0 z-0 pointer-events-none">
                    {/* Base gradient with depth */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-100/40 via-white to-accent-100/40" />

                    <div className="absolute top-0 left-0 w-full h-full">
                        {largeOrbs.map((item) => (
                            <motion.div
                                key={item.id}
                                className="absolute rounded-full"
                                style={{
                                    width: item.size,
                                    height: item.size,
                                    left: item.left,
                                    top: item.top,
                                    background: item.background,
                                    filter: item.filter,
                                }}
                                animate={item.animate}
                                transition={item.transition}
                            />
                        ))}

                        {mediumParticles.map((item) => (
                            <motion.div
                                key={item.id}
                                className="absolute rounded-full"
                                style={{
                                    width: item.size,
                                    height: item.size,
                                    left: item.left,
                                    top: item.top,
                                    background: item.background,
                                    filter: item.filter,
                                    boxShadow: item.boxShadow,
                                }}
                                animate={item.animate}
                                transition={item.transition}
                            />
                        ))}

                        {sparkles.map((item) => (
                            <motion.div
                                key={item.id}
                                className="absolute rounded-full"
                                style={{
                                    width: item.size,
                                    height: item.size,
                                    left: item.left,
                                    top: item.top,
                                    background: item.background,
                                    filter: item.filter,
                                    boxShadow: item.boxShadow,
                                }}
                                animate={item.animate}
                                transition={item.transition}
                            />
                        ))}

                        {!isMobile && shapes.map((item) => (
                            <motion.div
                                key={item.id}
                                className="absolute"
                                style={{
                                    width: item.size,
                                    height: item.size,
                                    left: item.left,
                                    top: item.top,
                                    borderRadius: item.borderRadius,
                                    border: item.border,
                                    filter: item.filter,
                                    boxShadow: item.boxShadow,
                                }}
                                animate={item.animate}
                                transition={item.transition}
                            />
                        ))}
                    </div>
                </div>

                {/* Enhanced Gradient Overlays for depth */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/60 to-white/90 z-10" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-transparent to-white/70 z-10" />

                {/* Content */}
                <div className="relative z-20 container-fluid text-center px-4 sm:px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="max-w-4xl mx-auto"
                    >
                        {/* Main Title */}
                        <motion.h1
                            className="hero-title mb-6 sm:mb-8"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <span className="block text-gradient font-black leading-tight">
                                Tu Salud Visual
                            </span>
                            <span className="block text-gray-900 font-black leading-tight">
                                y Dental
                            </span>
                            <span className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-600 mt-3 sm:mt-4">
                                En Manos Expertas
                            </span>
                        </motion.h1>

                        {/* Subtitle */}
                        <motion.p
                            className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            Tecnología de vanguardia, atención personalizada y años de experiencia
                            al servicio de tu bienestar en <span className="font-semibold text-primary-600">Toluca, México</span>.
                        </motion.p>

                        {/* CTA Buttons */}
                        <motion.div
                            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-12 sm:mb-16 px-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                        >
                            <motion.button
                                onClick={handleWhatsAppClick}
                                className={cn(
                                    'btn-primary text-sm sm:text-base lg:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto sm:min-w-[280px]',
                                    'bg-gradient-to-r from-primary-600 to-primary-700',
                                    'hover:from-primary-700 hover:to-primary-800',
                                    'shadow-2xl hover:shadow-primary-500/25',
                                    'transform hover:-translate-y-1 active:translate-y-0',
                                    'transition-all duration-300'
                                )}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <svg
                                    className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z" />
                                </svg>
                                <span className="whitespace-nowrap">Agenda tu Examen Visual</span>
                            </motion.button>

                            <motion.button
                                onClick={handleScrollToServices}
                                className={cn(
                                    'btn-secondary text-sm sm:text-base lg:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto sm:min-w-[250px]',
                                    'border-2 border-gray-800 text-gray-800',
                                    'hover:bg-gray-800 hover:text-white',
                                    'transform hover:-translate-y-1 active:translate-y-0',
                                    'transition-all duration-300'
                                )}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Conoce Nuestros Servicios
                            </motion.button>
                        </motion.div>

                        {/* Stats */}
                        <motion.div
                            className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-2xl mx-auto px-4"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                        >
                            <div className="text-center">
                                <div className="text-3xl sm:text-4xl font-black text-primary-600 mb-2">15+</div>
                                <div className="text-sm sm:text-base text-gray-600 font-medium">Años de Experiencia</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl sm:text-4xl font-black text-primary-600 mb-2">2</div>
                                <div className="text-sm sm:text-base text-gray-600 font-medium">Especialidades Médicas</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl sm:text-4xl font-black text-primary-600 mb-2">100%</div>
                                <div className="text-sm sm:text-base text-gray-600 font-medium">Satisfacción del Cliente</div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.5 }}
                >
                    <motion.div
                        className="flex flex-col items-center text-gray-500 cursor-pointer"
                        onClick={handleScrollToServices}
                        animate={{ y: [0, 10, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                    >
                        <span className="text-sm font-medium mb-2">Desliza para explorar</span>
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            />
                        </svg>
                    </motion.div>
                </motion.div>

                {/* Background Pattern */}
                <div className="absolute inset-0 z-0 opacity-5">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDY2Y2MiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSI0Ii8+PC9nPjwvZz48L3N2Zz4=')] bg-repeat"></div>
                </div>
            </section>
        </>
    )
}

export default Hero